
import React, { useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

interface MobileOptimizationProps {
  children: React.ReactNode;
}

const MobileOptimization: React.FC<MobileOptimizationProps> = ({ children }) => {
  const isMobile = useIsMobile();

  useEffect(() => {
    // Implement touch-friendly interactions
    if (isMobile) {
      // Add touch delay reduction
      document.documentElement.style.setProperty('touch-action', 'manipulation');
      
      // Optimize scroll behavior
      document.documentElement.style.setProperty('scroll-behavior', 'smooth');
      
      // Prevent zoom on input focus for iOS
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
      }
    }

    return () => {
      // Cleanup
      if (isMobile) {
        document.documentElement.style.removeProperty('touch-action');
        document.documentElement.style.removeProperty('scroll-behavior');
      }
    };
  }, [isMobile]);

  useEffect(() => {
    // Implement lazy loading for images below the fold
    const images = document.querySelectorAll('img[data-src]');
    
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            img.classList.add('fade-in');
          }
          imageObserver.unobserve(img);
        }
      });
    }, {
      rootMargin: '50px 0px',
      threshold: 0.1
    });

    images.forEach(img => imageObserver.observe(img));

    return () => {
      images.forEach(img => imageObserver.unobserve(img));
    };
  }, []);

  return (
    <div className={`mobile-optimized ${isMobile ? 'mobile-view' : 'desktop-view'}`}>
      {children}
    </div>
  );
};

export default MobileOptimization;
